import { BenefitType } from '@zorro/clients';
import { Box, FormErrorMessage, Stack, Text } from '@zorro/web/design-system';
import {
  CardSelector,
  PaymentMethodStatus,
  getPaymentMethodSelectOptions,
  useAnalytics,
  useLynxPaymentsContext,
  usePaymentMethodForm,
} from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';

import { EmployeePageTabType } from '../../EmployeePage/EmployeePageContent';
import { PaymentDetails } from '../../Payments/PaymentDetails/PaymentDetails';
import { PaymentMethodViewer } from '../../Payments/PaymentDetails/paymentMethodDisplayRules';
import {
  CarrierApplicationModalStepType,
  CarrierApplicationStep,
} from '../CarrierApplicationModal';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  onSubmit: () => void;
  tab: EmployeePageTabType;
};

export const CarrierApplicationPaymentMethodStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
  onSubmit,
  tab,
}: Props) => {
  const { sendEvent } = useAnalytics();

  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const benefit = benefits?.allBenefits.find(({ id }) => id === benefitId);
  const isMedical = benefit?.type === BenefitType.MAJOR_MEDICAL;

  const { paymentsProvider, isAccountActive } = useLynxPaymentsContext({
    employerId,
    employeeId,
  });

  const {
    isLoading,
    isRefetching,
    hasChangedPaymentMethodType,
    paymentMethodForm,
    paymentMethodStatus,
    onSubmit: onSubmitPaymentMethod,
    submitLabel,
  } = usePaymentMethodForm({
    employeeId,
    onboardingPeriodId,
    shouldShowLoadingOverlay: false,
    paymentsProvider,
    isAccountActive,
    benefitId,
  });

  const {
    formState: { errors },
  } = paymentMethodForm;

  const isDataLoading = paymentMethodStatus === PaymentMethodStatus.LOADING;

  const handleSubmit = async () => {
    if (!isMedical) {
      onSubmit();
      return { navigate: true };
    }

    if (!paymentsProvider) return { navigate: false };

    if (await paymentMethodForm.trigger()) {
      const navigate = await onSubmitPaymentMethod();
      if (navigate) onSubmit();
      return { navigate };
    }
    return { navigate: false };
  };

  if (!isMedical) {
    return (
      <CarrierApplicationStep
        step={CarrierApplicationModalStepType.PAYMENT_METHOD}
        employerId={employerId}
        employeeId={employeeId}
        onboardingPeriodId={onboardingPeriodId}
        benefitId={benefitId}
        onSubmit={handleSubmit}
        submitLabel="Continue"
        withReviewCard
        tab={tab}
      >
        <Text size="xl" fw={600}>
          Not supported for supplementals
        </Text>
      </CarrierApplicationStep>
    );
  }

  return (
    <CarrierApplicationStep
      step={CarrierApplicationModalStepType.PAYMENT_METHOD}
      employerId={employerId}
      employeeId={employeeId}
      onboardingPeriodId={onboardingPeriodId}
      benefitId={benefitId}
      onSubmit={handleSubmit}
      submitLabel={submitLabel}
      isSubmitDisabled={isLoading || isRefetching || !paymentsProvider}
      withReviewCard
      tab={tab}
    >
      {paymentsProvider && (
        <>
          <Stack gap="md">
            <Text fw={600}>Verify and generate the payment method</Text>
            <CardSelector
              form={paymentMethodForm}
              selectData={getPaymentMethodSelectOptions()}
              fieldName="paymentMethodType"
              label=""
              isLoading={isDataLoading}
              onSendEvent={async (fromMethod: string, toMethod: string) => {
                await sendEvent('change_payment_method_type', {
                  onboarding_period_id: onboardingPeriodId,
                  from_method: fromMethod,
                  to_method: toMethod,
                });
              }}
            />
          </Stack>
          <PaymentDetails
            viewer={PaymentMethodViewer.OPS}
            employeeId={employeeId}
            employerId={employerId}
            onboardingPeriodId={onboardingPeriodId}
            paymentMethodForm={paymentMethodForm}
            hasChangedPaymentMethodType={hasChangedPaymentMethodType}
          />
          {errors && (
            <Box>
              <FormErrorMessage errors={errors} fieldName="premiumAmount" />
              <FormErrorMessage errors={errors} fieldName="zorroPayAmount" />
              <FormErrorMessage errors={errors} fieldName="selfPayAmount" />
            </Box>
          )}
        </>
      )}
    </CarrierApplicationStep>
  );
};
